/**
 * The words this app uses, resolved once for the whole shell.
 *
 * Two separate questions, deliberately kept separate because they have different owners
 * (ux.md §2, and `packages/localization`'s own comment):
 *
 * - **Which language.** The person's own saved choice first, then what their browser asks for,
 *   then the community's default — but only from the set the community has actually enabled. A
 *   community that has not published French does not get served French because a volunteer's
 *   phone asked for it.
 * - **Which words.** Terminology is the community's, not the locale's. A community running the
 *   English interface may still call a competition a *special* and a round a *heat*, so
 *   `terminology_json` layers on top of whichever language was chosen.
 *
 * The community's settings need `community.manage` to read, and most people signed into this app
 * do not hold it. That is not a reason to fail: the provider asks, and a refusal simply means the
 * defaults stand. Nothing on screen waits for the answer — the first paint uses the person's own
 * locale, and the terminology arrives a moment later if it is theirs to see.
 */

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  LOCALES,
  createTranslator,
  isLocale,
  negotiateLocale,
  parseSupportedLocales,
  parseTermOverrides,
  type Locale,
  type Translator,
} from "@powwow/localization";
import { api, type TermOverride } from "./api.js";
import { useSession } from "./session.js";

const TranslationContext = createContext<Translator | null>(null);

export function TranslationProvider({ children }: { children: ReactNode }) {
  const { me, community } = useSession();
  const [settings, setSettings] = useState<{
    defaultLocale: string;
    supported: Locale[];
    terminology: Record<string, TermOverride | string> | null;
  } | null>(null);

  const communityId = community?.id ?? null;
  const canReadSettings = community?.permissions.includes("community.manage") ?? false;

  useEffect(() => {
    if (!communityId || !canReadSettings) {
      setSettings(null);
      return;
    }
    let cancelled = false;
    void api
      .community(communityId)
      .then((result) => {
        if (cancelled) return;
        setSettings({
          defaultLocale: result.community.defaultLocale,
          supported: parseSupportedLocales(result.community.supportedLocalesJson),
          terminology: result.community.terminologyJson,
        });
      })
      // A refusal or a dropped request means the defaults stand. Terminology is a courtesy layer;
      // an app that would not render without it would be an app that fails on a bad connection.
      .catch(() => {
        if (!cancelled) setSettings(null);
      });
    return () => {
      cancelled = true;
    };
  }, [communityId, canReadSettings]);

  const translator = useMemo(() => {
    const preferred = me?.authenticated ? me.user.locale : null;
    const acceptLanguage = typeof navigator === "undefined" ? null : navigator.language;

    // Where the community's published set is known, it bounds the choice — offering a
    // half-translated interface is worse than an honest English one, and which languages are ready
    // is the community's call (§32). Where it is *not* known — most people cannot read community
    // settings — the person's own choice wins over guessing on their behalf, because refusing
    // someone French because we could not read a settings row they have no permission for would be
    // the software making their decision for them.
    const locale = settings
      ? negotiateLocale({
          preferred,
          acceptLanguage,
          supported: settings.supported,
          fallback: settings.defaultLocale,
        })
      : negotiateLocale({ preferred, acceptLanguage, supported: LOCALES });
    return createTranslator({
      locale,
      terms: settings?.terminology ? parseTermOverrides(settings.terminology) : null,
    });
  }, [me, settings]);

  return <TranslationContext.Provider value={translator}>{children}</TranslationContext.Provider>;
}

/**
 * The translator for the current screen.
 *
 * Falls back to a default-locale translator rather than throwing when there is no provider, so a
 * screen rendered outside the shell — the judge's, the sign-in page — still reads words rather
 * than keys.
 */
export function useT(): Translator {
  const value = useContext(TranslationContext);
  return useMemo(() => value ?? createTranslator(), [value]);
}

/** Whether a stored locale string is one this build can actually render. */
export function knownLocale(value: string | null | undefined): value is Locale {
  return isLocale(value);
}
